import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, Linkedin, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useCMS } from "@/context/CMSContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { content } = useCMS();
  const contact = content.contact;
  const about = content.about;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      toast.success("Subscribed!", {
        description: response.data.message,
      });
      setEmail("");
    } catch (error) {
      toast.error("Subscription Failed", {
        description: error.response?.data?.detail || "Please try again.",
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "Courses", href: "#courses" },
    { name: "Why Choose Us", href: "#why-us" },
    { name: "Placements", href: "#placements" },
    { name: "Testimonials", href: "#testimonials" },
    { name: "Contact Us", href: "#enquiry" },
  ];

  const courses = [
    "Diploma in Hotel Management",
    "Bachelor of Hotel Management",
    "Master of Hotel Management",
    "Certificate Courses",
    "Short-term Programs",
  ];

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Youtube, href: "#", label: "YouTube" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
  ];

  // Format phone for display and tel link
  const displayPhone = contact.phone || "+91 7055547000";
  const telPhone = displayPhone.replace(/\s/g, "");

  return (
    <footer 
      className="bg-[hsl(345,65%,25%)] relative pb-24 lg:pb-8" 
      data-testid="footer"
      itemScope 
      itemType="https://schema.org/LocalBusiness"
    >
      {/* Hidden SEO Footer Content */}
      <div className="sr-only">
        <p itemProp="description">Shri Ram Institute of Hotel Management is the best hotel management college 
        in Dehradun, Uttarakhand. Located on Rajpur Road, Dehradun, we serve students from all over North India 
        including Uttarakhand, Uttar Pradesh, Delhi NCR, Haryana, Punjab, and Himachal Pradesh. 
        With 25+ years of excellence and 7000+ alumni placed at Taj, Oberoi, ITC, Hyatt, and Marriott hotels worldwide.</p>
        <span itemProp="name">Shri Ram Institute of Hotel Management Dehradun</span>
        <span itemProp="telephone">+91-7055547000</span>
        <span itemProp="email">admissions.raminstitute@gmail.com</span>
        <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
          <span itemProp="streetAddress">Rajpur Road</span>,
          <span itemProp="addressLocality">Dehradun</span>,
          <span itemProp="addressRegion">Uttarakhand</span>
          <span itemProp="postalCode">248001</span>,
          <span itemProp="addressCountry">India</span>
        </span>
        <span itemProp="areaServed">Dehradun, Uttarakhand, North India, Delhi NCR</span>
      </div>

      {/* Top Border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent" />

      {/* Newsletter Section */}
      <div className="bg-[hsl(345,60%,30%)] py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <h3 className="font-display text-2xl text-white mb-2">
                Stay Updated
              </h3>
              <p className="text-white/70">
                Subscribe for admissions updates and hospitality insights
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full max-w-md gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white"
                required
                data-testid="newsletter-email"
              />
              <Button variant="elegant" type="submit" disabled={isSubscribing} className="bg-white text-[hsl(345,60%,35%)] hover:bg-white/90" data-testid="newsletter-submit">
                {isSubscribing ? (
                  <Send className="w-5 h-5 animate-pulse" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Column 1 - About */}
          <div>
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg">
                <span className="text-[hsl(345,60%,35%)] font-display font-bold text-xl">SR</span>
              </div>
              <div>
                <h4 className="font-display text-lg font-semibold text-white">
                  Shri Ram Institute
                </h4>
                <p className="text-xs text-white/70">Hotel Management</p>
              </div>
            </div>

            <p className="text-white/70 text-sm mb-6 leading-relaxed">
              {about.description || "Shaping future hospitality leaders since 1999. Our industry-focused curriculum and exceptional placement record make us Dehradun's premier hotel management institute."}
            </p>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-white transition-colors duration-300 text-sm flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Courses */}
          <div>
            <h4 className="font-display text-lg font-semibold text-white mb-6">
              Our Courses
            </h4>
            <ul className="space-y-3">
              {courses.map((course) => (
                <li key={course}>
                  <a
                    href="#courses"
                    className="text-white/70 hover:text-white transition-colors duration-300 text-sm flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {course}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Contact (Dynamic from CMS) */}
          <div>
            <h4 className="font-display text-lg font-semibold text-white mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-white/70 mt-0.5 flex-shrink-0" />
                {contact.mapLink ? (
                  <a 
                    href={contact.mapLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                    data-testid="footer-address"
                  >
                    {contact.address || "Dehradun, Uttarakhand, India - 248001"}
                  </a>
                ) : (
                  <span className="text-white/70 text-sm" data-testid="footer-address">
                    {contact.address || "Dehradun, Uttarakhand, India - 248001"}
                  </span>
                )}
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-white/70 flex-shrink-0" />
                <a 
                  href={`tel:${telPhone}`} 
                  onClick={() => window.trackClickConversion && window.trackClickConversion('phone_call', 'Footer - Call')}
                  className="text-white/70 hover:text-white text-sm transition-colors" 
                  data-testid="footer-phone"
                >
                  {displayPhone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-white/70 flex-shrink-0" />
                <a href={`mailto:${contact.email || "admissions.raminstitute@gmail.com"}`} className="text-white/70 hover:text-white text-sm transition-colors" data-testid="footer-email">
                  {contact.email || "admissions.raminstitute@gmail.com"}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-white/70 flex-shrink-0" />
                <span className="text-white/70 text-sm">
                  Mon - Sat: 9:00 AM - 6:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Shri Ram Institute of Hotel Management. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <a href="/privacy-policy" className="text-white/50 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/privacy-policy" className="text-white/50 hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Admissions Banner */}
      <div className="bg-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[hsl(345,60%,35%)] font-semibold">
            🎓 Admissions Open for 2026 Batch • Limited Seats Available • {' '}
            <button
              onClick={() => {
                // Track conversion
                if (window.trackClickConversion) {
                  window.trackClickConversion('footer_apply', 'Footer - Apply Now');
                }
                // Scroll to callback form after Oberoi section
                const callbackSection = document.getElementById('callback-after-oberoi');
                if (callbackSection) {
                  callbackSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="underline hover:text-[hsl(345,60%,25%)] cursor-pointer font-bold"
              data-testid="footer-apply-now"
            >
              Apply Now!
            </button>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
